import { MKLog } from "./MKLog";
import { MKHttpRequest } from "../request";
import { MKCustomError } from "../../constants";
import { MKHttpError } from "../error";
import { MKImageMediaPlaylist, MKThumbnail, MKHttpResponse } from "../../interfaces";
import { MKHeaders } from "../../alias";
import { MKUtils } from "./MKUtils";

/**
 * Thumbnail manager class.
 * 
 * For the given manifest url, parses the manifest and builds a list of thumbnail objects which the caller can fetch to render thumbnail preview onscreen.
 * 
 * @internal
 */
export class MKThumbnailManager {
    private httpRequest: MKHttpRequest;                           // HTTP Request utility
    private manifestUrl: string;                                  // master manifest URL
    private headers?: MKHeaders;                                  // HTTP Request headers
    private imageMediaPlaylists: MKImageMediaPlaylist[];          // List of image media playlists obtained after parsing master manifest
    private thumbnails: MKThumbnail[];                            // List of image media objects parsed from the playlist
    private isEnabled: boolean;                                   // Parsing image media playlist is enabled or disabled?
    private currentPlaylistData: string;                          // Currently parsed image media playlist data (raw response text)
    private targetDuration: number;                               // Maxmimum media segment duration of the playlist
    private reloadPlaylistTimer?: ReturnType<typeof setTimeout>;  // Handle to the timer for reloading media playlist
    private retryCount: number;                                   // Current retry count
    private static readonly PLAYLIST_MAX_RETRY_COUNT = 3;         // Max retries to download and process playlist on error (4xx/5xx errors)

    /**
     * Create a new instance of thumbnail manager.
     * 
     * @param manifestUrl the HLS master manifest url.
     */
    constructor() {
        this.httpRequest = new MKHttpRequest();
        this.manifestUrl = "";
        this.imageMediaPlaylists = [];
        this.thumbnails = [];
        this.isEnabled = false;
        this.currentPlaylistData = "";
        this.targetDuration = 0;
        this.retryCount = 0;
    }

    ////////////////////////////////////////////////////////
    // Exposed methods
    ////////////////////////////////////////////////////////

    /**
     * Get thumbnail for the specified time offset.
     *
     * @param time time offset for which the thumbnail is required. The time offset is expected to be a relative offset within the available seekable range.
     * @return a thumbnail object if found or `null` otherwise.
     */
    getThumbnail(time: number): MKThumbnail | null {
        if (!this.isEnabled) {
            MKLog.warn("[MKThumbnailManager] Thumbnails not enabled or available yet!");
            return null;
        }

        const index = this.thumbnails.findIndex((thumbnail) => {
            return (time >= thumbnail.start && time <= thumbnail.end);
        });

        if (index >= 0) {
            return this.thumbnails[index];
        }

        return null;
    }

    /**
     * Start fetching and processing thumbnails from the HLS manifest if available.
     */
    enable(manifestUrl: string, headers?: MKHeaders) {
        if (this.isEnabled) {
            // disable current processing before continuing...
            this.disable();
        }

        // set the manifestUrl and headers if any provided for use
        this.manifestUrl = manifestUrl;
        this.headers = headers;

        // fetch image media playlists if available in the HLS manifest
        this.fetchImageMediaPlaylists()
        .then(() => {
            if (this.imageMediaPlaylists.length > 0 && this.imageMediaPlaylists[0]) {
                // Processing the first available playlist for now...
                this.fetchAndProcessImageMediaPlaylist(this.imageMediaPlaylists[0])
                .then(() => {
                    // Ready to handle thumbnail requests
                    this.isEnabled = true;
                    MKLog.debug("[MKThumbnailManager] Ready to serve thumbnails.");
                })
                .catch(() => {
                    MKLog.warn("[MKThumbnailManager] Failed to get images from playlist!");
                });
            }
        })
        .catch(() => {
            MKLog.warn("[MKThumbnailManager] Failed to get image media playlist from master manifest!");
        });
    }

    /**
     * Disable thumbnail processing if enabled.
     */
    disable() {
        // cancel timer if active
        if (this.reloadPlaylistTimer) {
            clearTimeout(this.reloadPlaylistTimer);
            this.reloadPlaylistTimer = undefined;
        }

        // cancel any ongoing requests
        this.httpRequest.cancelRequest();

        // reset all other variables & flags
        this.manifestUrl = "";
        this.headers = undefined;
        this.imageMediaPlaylists = [];
        this.thumbnails = [];
        this.isEnabled = false;
        this.currentPlaylistData = "";
        this.targetDuration = 0;
        this.retryCount = 0;
    }

    /**
     * Set Http request headers.
     *
     * @param headers http headers to set.
     */
    setHttpHeaders(headers: MKHeaders | null): void {
        this.headers = headers ?? undefined;
    }

    ////////////////////////////////////////////////////////
    // Private methods
    ////////////////////////////////////////////////////////

    /**
     * Fetch image media playlist and parse to extract thumbnail information.
     *
     * @param playlist the image media playlist to process.
     */
    private async fetchAndProcessImageMediaPlaylist(playlist: MKImageMediaPlaylist): Promise<void> {
        try {
            if (!!playlist.uri) {
                const playlistData = await this.fetchManifest(playlist.uri);
                if (!!playlistData) {
                    // local variables
                    let tilesPlaylist = false;             // indicates whether the segment contains tile image or not
                    let tilesInfo: TilesInfo = {           // if the playlist contains tiles, this object contains the tile image related information
                        duration: 0,
                        width: 0,
                        height: 0,
                        rows: 0,
                        columns: 0
                    };
                    let startTime = 0;                     // start time of the image
                    let endTime = 0;                       // end time of the image
                    let mediaDuration = 0;                 // current media segment duration
                    let thumbnails: MKThumbnail[] = [];    // list of parsed thumbnail image object
                    let reloadPlaylist = true;             // should reload the playlist or not?
                    let reloadPlaylistTimerTimeout = 0;    // time after which the playlist should be reloaded

                    // If the playlist is different from the current playlist
                    if (this.currentPlaylistData !== playlistData) {
                        // update current playlist
                        this.currentPlaylistData = playlistData;

                        // parse the manfest data line by line
                        const manifestLines = playlistData.split(/\n+/m);
                        manifestLines.forEach((line) => {
                            if (line.startsWith("#EXT-X-TARGETDURATION")) {
                                this.targetDuration = parseInt(line.split(":")[1] ?? "0", 10);
                            }
                            if (line === "#EXT-X-PLAYLIST-TYPE:VOD" || line === "#EXT-X-ENDLIST") {
                                reloadPlaylist = false;
                                reloadPlaylistTimerTimeout = 0;
                            }
                            if (line.startsWith("#EXTINF")) {
                                startTime = endTime; // previous media/segment end time is current media/segment start time
                                mediaDuration = parseFloat(line.split(":")[1] ?? "0.0");
                                endTime = parseFloat((endTime + mediaDuration).toFixed(3));
                                if (reloadPlaylist) {
                                    // we MUST wait for at least the duration of the last segment in the 
                                    // Playlist before attempting to reload the Playlist file again.
                                    reloadPlaylistTimerTimeout = mediaDuration;
                                }
                            }
                            if (line.startsWith("#EXT-X-TILES")) {
                                tilesPlaylist = true;
                                tilesInfo.duration = line.includes("DURATION=") ? parseFloat(line.split("DURATION=")[1]?.split(",")[0] ?? "0.0") : 0.0;
                                let resolution = line.includes("RESOLUTION=") ? line.split("RESOLUTION=")[1]?.split(",")[0] ?? "" : "";
                                if (!!resolution && resolution.includes("x")) {
                                    tilesInfo.width = parseInt(resolution.split("x")[0] ?? "0", 10);
                                    tilesInfo.height = parseInt(resolution.split("x")[1] ?? "0", 10); 
                                }
                                let layout = line.includes("LAYOUT=") ? line.split("LAYOUT=")[1]?.split(",")[0] ?? "" : "";
                                if (!!layout && layout.includes("x")) {
                                    tilesInfo.columns = parseInt(layout.split("x")[0] ?? "0", 10); 
                                    tilesInfo.rows = parseInt(layout.split("x")[1] ?? "0", 10);
                                }
                            }
                            if (!line.startsWith("#")) {
                                if (tilesPlaylist) { // Each URL is a grid/tile of thumbnail images
                                    let tileImageStartTime = startTime;
                                    tileLoop:
                                    for (let row = 0; row < tilesInfo.rows; row++) {
                                        for (let col = 0; col < tilesInfo.columns; col++) {
                                            // update end time
                                            let tileImageEndTime = parseFloat((tileImageStartTime + tilesInfo.duration).toFixed(3));

                                            // prepare the thumbnail object
                                            const thumbnail: MKThumbnail = {
                                                end: tileImageEndTime,
                                                height: tilesInfo.height,
                                                start: tileImageStartTime,
                                                text: "",
                                                url: new URL(line, this.manifestUrl).toString(),
                                                width: tilesInfo.width,
                                                x: col * tilesInfo.width,
                                                y: row * tilesInfo.height
                                            };

                                            // append thumbnail object to the list
                                            thumbnails.push(thumbnail);

                                            // update start time and check for boundary cross
                                            tileImageStartTime = tileImageEndTime;
                                            if (tileImageEndTime >= endTime) {
                                                // don't process images beyond the segment/media boundary
                                                break tileLoop;
                                            }
                                        }
                                    }
                                } else { // Each URL is an individual thumbnail image
                                    // prepare the thumbnail object
                                    const thumbnail: MKThumbnail = {
                                        end: endTime,
                                        height: playlist.height,
                                        start: startTime,
                                        text: "",
                                        url: new URL(line, this.manifestUrl).toString(),
                                        width: playlist.width,
                                        x: 0,
                                        y: 0
                                    };

                                    // append thumbnail object to the list
                                    thumbnails.push(thumbnail);
                                }
                            }
                        });

                        // update the collected thumbnails
                        if (thumbnails.length > 0) {
                            this.thumbnails = thumbnails;
                        }
                    } else {
                        // If the playlist data remains unchanged (previous & current playlist are same)
                        // then we MUST wait for a period of one-half the Target Duration before retrying
                        reloadPlaylistTimerTimeout = (this.targetDuration / 2);
                    }

                    // Reload the playlist as the current playlist is not final.
                    if (reloadPlaylist && reloadPlaylistTimerTimeout > 0) {
                        this.reloadImageMediaPlaylist(playlist, reloadPlaylistTimerTimeout);
                    }
                } else {
                    throw new Error("Invalid image playlist data.");
                }
            } else {
                throw new Error("Invalid image playlist url.")
            }
        } catch (error) {
            if (error instanceof MKHttpError) {
                if (error.code === MKCustomError.HttpAbort.code) {
                    // do nothing for abort errors
                    return; 
                }

                // for HTTP 4xx/5xx errors we need to retry until max retry count and then give up!
                const errorCode = parseInt(error.code);
                if (errorCode >= 400 || errorCode <= 599) {
                    this.refetchAndProcessImageMediaPlaylistOnError(playlist);
                }
            } else {
                throw error;
            }
        }
    }

    /**
     * Reload the image media playlist after specified timeout.
     *
     * @param playlist the playlist to reload.
     * @param timeout timeout after which to reload the playlist.
     */
    private reloadImageMediaPlaylist(playlist: MKImageMediaPlaylist, timeout: number) {
        // convert to seconds
        const timeoutSeconds = timeout * 1000;

        // cancel timer if active
        if (this.reloadPlaylistTimer) {
            clearTimeout(this.reloadPlaylistTimer);
            this.reloadPlaylistTimer = undefined;
        }

        // schedule timer
        this.reloadPlaylistTimer = setTimeout(() => {
            this.fetchAndProcessImageMediaPlaylist(playlist)
            .catch(() => {
                MKLog.warn("[MKThumbnailManager] Failed to get images from playlist!");
            });
        }, timeoutSeconds);
    }

    /**
     * Refetch and process image media playlist when the request fails with HTTP 4xx/5xx errors.
     *
     * Max of {@link MKThumbnailManager.PLAYLIST_MAX_RETRY_COUNT} attempts will be tried before giving up.
     *
     * Note: currently the same playlist is reattempted and different varients if available are not tried.
     *
     * @param playlist the image media playlist.
     */
    private refetchAndProcessImageMediaPlaylistOnError(playlist: MKImageMediaPlaylist) {
        if (this.retryCount < MKThumbnailManager.PLAYLIST_MAX_RETRY_COUNT) {
            this.retryCount++;
            const retryAfter = this.retryCount * 2;
            MKLog.warn(`[MKThumbnailManager] Playlist retry attempt: ${this.retryCount} in ${retryAfter} seconds.`);
            this.reloadImageMediaPlaylist(playlist, retryAfter);
        } else {
            MKLog.warn(`[MKThumbnailManager] Reached max retry limit for playlist with URL: ${playlist.uri}`);
            this.disable();
        }
    }

    /**
     * Fetch master manifest and parse to extract image media playlist information.
     */
    private async fetchImageMediaPlaylists(): Promise<void> {
        try {
            this.imageMediaPlaylists = [];
            const manifestData = await this.fetchManifest(this.manifestUrl);
            if (!!manifestData) {
                // parse the manifest data and extract the image media playlists
                const manifestLines = manifestData.split(/\n+/m);
                manifestLines.forEach((line) => {
                    if (!!line && line.trim().startsWith("#EXT-X-IMAGE-STREAM-INF")) {
                        // extract image playlist information
                        const playlistInfo = line.trim();
                        const bandwidth = playlistInfo.includes("BANDWIDTH=") ? parseInt(playlistInfo.split("BANDWIDTH=")[1]?.split(",")[0] ?? "0", 10) : 0;
                        const averageBandwidth = playlistInfo.includes("AVERAGE-BANDWIDTH=") ? parseInt(playlistInfo.split("AVERAGE-BANDWIDTH=")[1]?.split(",")[0] ?? "0", 10) : 0;
                        const resolution = playlistInfo.includes("RESOLUTION=") ? playlistInfo.split("RESOLUTION=")[1]?.split(",")[0] ?? "" : "";
                        let width = 0;
                        let height = 0;
                        if (!!resolution && resolution.includes("x")) {
                            width = parseInt(resolution.split("x")[0] ?? "0", 10);
                            height = parseInt(resolution.split("x")[1] ?? "0", 10);
                        }
                        const codecs = playlistInfo.includes("CODECS=") ? MKUtils.jsonParse(playlistInfo.split("CODECS=")[1]?.split(",")[0] ?? "") : "";
                        const uri = new URL(playlistInfo.includes("URI=") ? MKUtils.jsonParse(playlistInfo.split("URI=")[1]?.split(",")[0] ?? "") : "", this.manifestUrl).toString();

                        // add image playlist to the list
                        const imagePlaylist: MKImageMediaPlaylist = {
                            bandwidth: bandwidth,
                            averageBandwidth: averageBandwidth,
                            resolution: resolution,
                            width: width,
                            height: height,
                            codecs: codecs,
                            uri: uri
                        }
                        this.imageMediaPlaylists.push(imagePlaylist);
                    }
                });
            } else {
                throw new Error("Invalid manfiest data.")
            }
        } catch (error) {
            if (error instanceof MKHttpError && error.code === MKCustomError.HttpAbort.code) {
                // do nothing for abort errors
                return;
            }
            throw error;
        }
    }

    /**
     * Fetch the given manifest.
     * 
     * @param manifestUrl `master` or `variant` manifest url.
     * @returns a promise which resolves with the manifest data as `string` or rejects with error object.
     */
    private async fetchManifest(manifestUrl: string): Promise<string> {
        return this.httpRequest.get(manifestUrl, {headers: this.headers})
        .then((response: MKHttpResponse) => {
            if (!response.ok) {
                // HTTP 4xx/5xx error codes
                throw new MKHttpError({
                    code: response.status.toString(),
                    message: MKCustomError.Http.message
                });
            }
            return response.text();
        })
        .then((responseText: string) => {
            return responseText.replace(/\r\n|\r(?=[^\n]|$)/gm, '\n').trim();
        })
        .catch((error) => {
            throw error;
        });
    }
}

/**
 * Information read from #EXT-X-TILES tag in the playlist.
 * 
 * Example: #EXT-X-TILES:RESOLUTION=144x80,LAYOUT=5x2,DURATION=10.0100
 *
 * @internal
 */
interface TilesInfo {
    /**
     * Duration in seconds for which each image in the tile.
     */
    duration: number;

    /**
     * Resolution width of the image in pixels.
     */
    width: number;

    /**
     * Resolution height of the image in pixels.
     */
    height: number;

    /**
     * Number of rows (describing the tile layout).
     */
    rows: number;

    /**
     * Number of columns (describing the tile layout).
     */
    columns: number
}